import { useState, useCallback, useMemo } from 'react'
import { Table, Tag, Button, Select, App } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { ExpandOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import type { FeedbackWithNotes, FeedbackNote, FeedbackStatus, FeedbackPriority } from '@/types/feedback.types'
import { FeedbackStatusBadge } from './FeedbackStatusBadge'
import { FeedbackExpandedRow } from './FeedbackExpandedRow'
import { feedbackService } from '@/services/feedbackService'

const STATUS_OPTIONS: FeedbackStatus[] = ['New', 'In Progress', 'Duplicate', 'Resolved']
const PRIORITY_OPTIONS: (FeedbackPriority | null)[] = [null, 'Low', 'Medium', 'High', 'Critical']

dayjs.extend(relativeTime)

// Format feedback ID as FB-##### string
const formatFeedbackId = (feedbackId?: number): string => {
  if (!feedbackId) return '-'
  return `FB-${feedbackId}`
}

export interface FeedbackTableProps {
  data: FeedbackWithNotes[]
  isAdminView: boolean
  loading?: boolean
  onRefresh?: () => void
}

export const FeedbackTable: React.FC<FeedbackTableProps> = ({
  data,
  isAdminView,
  loading,
  onRefresh,
}) => {
  const { message } = App.useApp()

  // Track notes per feedback item
  const [notesCache, setNotesCache] = useState<Record<string, FeedbackNote[]>>({})
  const [loadingNotes, setLoadingNotes] = useState<Record<string, boolean>>({})
  const [updatingField, setUpdatingField] = useState<Record<string, string>>({})

  // Handle inline field updates (status, priority)
  const handleInlineUpdate = useCallback(async (
    record: FeedbackWithNotes,
    field: 'status' | 'priority',
    value: string | null
  ) => {
    const fieldKey = `${record.uid}-${field}`
    setUpdatingField((prev) => ({ ...prev, [fieldKey]: field }))

    const result = await feedbackService.updateFeedback({
      uid: record.uid,
      version: record.version,
      [field]: value,
    })

    setUpdatingField((prev) => {
      const updated = { ...prev }
      delete updated[fieldKey]
      return updated
    })

    if (!result.success) {
      message.error(`Failed to update ${field}`)
      return
    }

    message.success(`${field.charAt(0).toUpperCase() + field.slice(1)} updated`)
    onRefresh?.()
  }, [onRefresh])

  // Fetch notes for a record
  const fetchNotes = useCallback(async (recordUid: string) => {
    setLoadingNotes((prev) => ({ ...prev, [recordUid]: true }))
    try {
      const result = await feedbackService.getNotes(recordUid)
      if (result.data) {
        setNotesCache((prev) => ({ ...prev, [recordUid]: result.data! }))
      }
    } catch (error) {
      console.error('Failed to fetch notes:', error)
    } finally {
      setLoadingNotes((prev) => ({ ...prev, [recordUid]: false }))
    }
  }, [])

  // Fetch notes when row expands
  const handleExpand = useCallback(async (expanded: boolean, record: FeedbackWithNotes) => {
    if (!expanded) return
    if (notesCache[record.uid]) return // Already loaded
    await fetchNotes(record.uid)
  }, [notesCache, fetchNotes])

  // Merge notes into record for expanded row
  const getRecordWithNotes = useCallback((record: FeedbackWithNotes): FeedbackWithNotes => {
    return {
      ...record,
      notes: notesCache[record.uid] || [],
    }
  }, [notesCache])

  // Build columns based on admin/customer view
  const columns: ColumnsType<FeedbackWithNotes> = useMemo(() => [
    {
      title: 'ID',
      dataIndex: 'feedbackId',
      key: 'feedbackId',
      width: 100,
      render: (_, record) => (
        <span
          style={{ fontFamily: 'monospace', cursor: 'pointer' }}
          onClick={() => {
            navigator.clipboard.writeText(formatFeedbackId(record.feedbackId))
            message.success(`Copied ${formatFeedbackId(record.feedbackId)}`)
          }}
          title="Click to copy"
        >
          {formatFeedbackId(record.feedbackId)}
        </span>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'feedbackType',
      key: 'feedbackType',
      width: 100,
      render: (_, record) => {
        const type = record.feedbackType
        const color = type === 'Enhancement' ? 'blue' : type === 'Bug' ? 'orange' : 'red'
        return <Tag color={color}>{type}</Tag>
      },
    },
    ...(isAdminView ? [{
      title: 'Organization',
      dataIndex: 'orgName',
      key: 'orgName',
      width: 150,
      ellipsis: true,
      render: (_: any, record: FeedbackWithNotes) => record.orgName || record.orgId?.slice(0, 8) + '...',
    }] : []),
    ...(isAdminView ? [{
      title: 'User',
      dataIndex: 'email',
      key: 'email',
      width: 180,
      ellipsis: true,
      render: (_: any, record: FeedbackWithNotes) => record.userName || record.email || record.userId?.slice(0, 8) + '...',
    }] : []),
    {
      title: 'Description',
      dataIndex: 'feedbackComments',
      key: 'feedbackComments',
      width: 300,
      ellipsis: true,
    },
    ...(isAdminView ? [{
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 120,
      render: (_: any, record: FeedbackWithNotes) => (
        <Select
          value={record.priority}
          size="small"
          style={{ width: 110 }}
          loading={!!updatingField[`${record.uid}-priority`]}
          onChange={(value) => handleInlineUpdate(record, 'priority', value)}
          placeholder="Unset"
          allowClear
          options={PRIORITY_OPTIONS.filter(p => p !== null).map((p) => ({ label: p, value: p }))}
        />
      ),
    }] : []),
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: isAdminView ? 140 : 120,
      render: (_, record) =>
        isAdminView ? (
          <Select
            value={record.status}
            size="small"
            style={{ width: 130 }}
            loading={!!updatingField[`${record.uid}-status`]}
            onChange={(value) => handleInlineUpdate(record, 'status', value)}
            options={STATUS_OPTIONS.map((s) => ({ label: s, value: s }))}
          />
        ) : (
          <FeedbackStatusBadge status={record.status} />
        ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (_, record) => dayjs(record.createdAt).fromNow(),
    },
  ], [isAdminView, updatingField, handleInlineUpdate])

  return (
    <Table<FeedbackWithNotes>
      columns={columns}
      dataSource={data}
      rowKey="uid"
      loading={loading}
      pagination={{ defaultPageSize: 20 }}
      style={{ tableLayout: 'fixed' }}
      expandable={{
        onExpand: handleExpand,
        expandedRowRender: (record) => (
          <FeedbackExpandedRow
            record={getRecordWithNotes(record)}
            isAdminView={isAdminView}
            onUpdate={() => {
              // Re-fetch notes for this record
              fetchNotes(record.uid)
              onRefresh?.()
            }}
            notesLoading={loadingNotes[record.uid]}
          />
        ),
        expandIcon: ({ onExpand, record }) => (
          <Button
            type="text"
            size="small"
            icon={<ExpandOutlined />}
            onClick={(e) => onExpand(record, e)}
            {...(record.uid === data[0]?.uid ? { 'data-tour': 'feedback-expand-row' } : {})}
          />
        ),
      }}
    />
  )
}

export default FeedbackTable
